import { motion } from "framer-motion"
import {Crosshair, MousePointerClick, Palette, RefreshCw, Flame, Weight, Droplets, Ruler, RotateCcw, ShieldCheck} from 'lucide-react'
import { useLang } from "@/context/LanguageContext"

export default function SavoirFaireSection() {
  const { t, lang } = useLang()

  const steps = [
    {
      icon: Crosshair,
      titleFr: "Percez",
      titleEn: "Pierce",
      descFr: "Placez le pic à l'endroit souhaité sur votre vêtement",
      descEn: "Place the pin where you want it on your garment",
    },
    {
      icon: MousePointerClick,
      titleFr: "Clipsez",
      titleEn: "Clip",
      descFr: "Fixez l'embase de l'autre côté. Un simple clic.",
      descEn: "Attach the base on the other side. A simple click.",
    },
    {
      icon: Palette,
      titleFr: "Personnalisez",
      titleEn: "Customize",
      descFr: "Choisissez votre capsule parmi 28 couleurs",
      descEn: "Choose your cap from 28 colors",
    },
    {
      icon: RefreshCw,
      titleFr: "Changez",
      titleEn: "Change",
      descFr: "Intervertissez les capsules entre vos boutons de même taille, à volonté",
      descEn: "Swap caps between buttons of the same size, as often as you like",
    },
  ]

  const reasons = [
    {
      icon: Flame,
      titleFr: "Nylon 6/6",
      titleEn: "Nylon 6/6",
      descFr: "Résiste au fer à repasser et aux lavages professionnels",
      descEn: "Withstands ironing and professional laundering",
    },
    {
      icon: Weight,
      titleFr: "35 kg de pression",
      titleEn: "35 kg pressure",
      descFr: "Testé pour une résistance exceptionnelle",
      descEn: "Tested for exceptional strength",
    },
    {
      icon: Droplets,
      titleFr: "28 couleurs",
      titleEn: "28 colors",
      descFr: "Un arc-en-ciel de possibilités",
      descEn: "A rainbow of possibilities",
    },
    {
      icon: Ruler,
      titleFr: "2 tailles",
      titleEn: "2 sizes",
      descFr: "18 mm et 20 mm",
      descEn: "18 mm and 20 mm",
    },
    {
      icon: RotateCcw,
      titleFr: "Capsule interchangeable",
      titleEn: "Interchangeable cap",
      descFr: "Changez de couleur en un clic",
      descEn: "Change colour in one click",
    },
    {
      icon: ShieldCheck,
      titleFr: "Breveté en 1995",
      titleEn: "Patented in 1995",
      descFr: "Technologie brevetée en 1995",
      descEn: "Technology patented in 1995",
    },
  ]

  return (
    <section id="savoir-faire" className="relative py-32" style={{ backgroundColor: "#1C1917" }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <p className="text-xs tracking-[0.4em] uppercase mb-4" style={{ color: "rgba(225, 89, 63, 0.7)" }}>
            {t("Le produit", "The product")}
          </p>
          <h2 className="text-4xl md:text-5xl mb-6" style={{ color: "#F8F2EB", letterSpacing: "4px" }}>
            {t("Une innovation brevetée", "A patented innovation")}
          </h2>
          <div className="w-16 h-px mx-auto mb-6" style={{ backgroundColor: "rgba(225, 89, 63, 0.4)" }} />
          <p className="max-w-lg mx-auto leading-relaxed" style={{ color: "rgba(248, 242, 235, 0.5)", fontSize: "17px" }}>
            {t(
              "Clips'On est un bouton vestimentaire en 3 pièces : pic, embase et capsule interchangeable de couleur.",
              "Clips'On is a 3-piece clothing button \u2014 pin, base and interchangeable color cap."
            )}
          </p>
        </motion.div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, i) => (
            <motion.div
              key={lang === "fr" ? step.titleFr : step.titleEn}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="group text-center p-8 border transition-all duration-700"
              style={{ borderColor: "rgba(248, 242, 235, 0.06)", backgroundColor: "rgba(248, 242, 235, 0.03)" }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = "rgba(225, 89, 63, 0.25)")}
              onMouseLeave={e => (e.currentTarget.style.borderColor = "rgba(248, 242, 235, 0.06)")}
            >
              <div
                className="w-7 h-7 mx-auto mb-6 border flex items-center justify-center transition-all duration-500"
                style={{ borderColor: "rgba(225, 89, 63, 0.3)" }}
              >
                <step.icon size={11} strokeWidth={1} style={{ color: "#E1593F" }} />
              </div>
              <div
                className="w-6 h-6 mx-auto mb-4 rounded-full border flex items-center justify-center"
                style={{ borderColor: "rgba(225, 89, 63, 0.35)" }}
              >
                <span className="text-xs font-medium" style={{ color: "#E1593F" }}>{i + 1}</span>
              </div>
              <h3 className="text-xl mb-3" style={{ color: "#F8F2EB", letterSpacing: "4px" }}>
                {lang === "fr" ? step.titleFr : step.titleEn}
              </h3>
              <p className="leading-relaxed" style={{ color: "rgba(248, 242, 235, 0.4)", fontSize: "17px" }}>
                {lang === "fr" ? step.descFr : step.descEn}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Pourquoi Clips'On */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-24"
        >
          <p className="text-xs tracking-[0.4em] uppercase mb-8 text-center" style={{ color: "rgba(225, 89, 63, 0.7)" }}>
            {t("Pourquoi Clips'On ?", "Why Clips'On?")}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px" style={{ backgroundColor: "rgba(248, 242, 235, 0.06)" }}>
            {reasons.map((reason, i) => (
              <motion.div
                key={lang === "fr" ? reason.titleFr : reason.titleEn}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 + i * 0.08 }}
                className="p-6 text-center"
                style={{ backgroundColor: "#1C1917" }}
              >
                <reason.icon size={20} strokeWidth={1} className="mx-auto mb-3" style={{ color: "#E1593F" }} />
                <p className="mb-1" style={{ color: "#F8F2EB", fontSize: "17px" }}>
                  {lang === "fr" ? reason.titleFr : reason.titleEn}
                </p>
                <p className="leading-relaxed" style={{ color: "rgba(248, 242, 235, 0.35)", fontSize: "13px" }}>
                  {lang === "fr" ? reason.descFr : reason.descEn}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
